import { CountryPreferenceProduct } from './CountryPreferenceProduct'

export interface TickmillProductDto extends CountryPreferenceProduct {
  hubspotName: string
}

export enum TickmillProductType {
  CFD = 1,
  ETD = 2,
  STOCKS = 3,
  Perpetuals = 4,
}

export const TICKMILL_SUB_PRODUCT_TYPES = [TickmillProductType.Perpetuals] as const

export const isTickmillSubProductType = (
  value: number
): value is (typeof TICKMILL_SUB_PRODUCT_TYPES)[number] => {
  return TICKMILL_SUB_PRODUCT_TYPES.includes(value as (typeof TICKMILL_SUB_PRODUCT_TYPES)[number])
}

export const isCFDProductType = (value: TickmillProductType): boolean => {
  return value === TickmillProductType.CFD
}

export const isETDProductType = (value: TickmillProductType): boolean => {
  return value === TickmillProductType.ETD
}

export type ProductPickerValue = string

type ParsedValue =
  | { type: 'product'; id: number }
  | { type: 'standaloneSubProduct'; id: number }
  | { type: 'subProduct'; parentId: number; id: number }

interface ProductPickerOption {
  label: string
  value: ProductPickerValue
}

interface ProductPickerGroup {
  label: string
  options: ProductPickerOption[]
}

const cloneProduct = (product: CountryPreferenceProduct): CountryPreferenceProduct => ({
  ...product,
  subProducts: [...(product.subProducts ?? [])],
})

const parseValue = (value: ProductPickerValue): ParsedValue | null => {
  const parts = value.split(':')
  const [kind, a, b] = parts

  switch (kind) {
    case 'product':
      return a ? { type: 'product', id: Number(a) } : null

    case 'standalone-sub-product':
      return a ? { type: 'standaloneSubProduct', id: Number(a) } : null

    case 'sub-product':
      return a && b
        ? {
            type: 'subProduct',
            parentId: Number(a),
            id: Number(b),
          }
        : null

    default:
      return null
  }
}

const toProductValue = (id: number) => `product:${id}`
const toStandaloneSubProductValue = (id: number) => `standalone-sub-product:${id}`
const toSubProductValue = (parentId: number, id: number) => `sub-product:${parentId}:${id}`

const findById = (
  id: number,
  available: CountryPreferenceProduct[],
  existing: CountryPreferenceProduct[] = []
) => {
  const product =
    available.find((item) => item.id === id) ?? existing.find((item) => item.id === id)

  return product ? cloneProduct(product) : undefined
}

export const normalizeProduct = cloneProduct

export const getProductPickerGroups = (
  availableProducts: CountryPreferenceProduct[]
): ProductPickerGroup[] => {
  const products = availableProducts.map(cloneProduct)
  const subProducts = products.filter((product) =>
    TICKMILL_SUB_PRODUCT_TYPES.includes(product.id as (typeof TICKMILL_SUB_PRODUCT_TYPES)[number])
  )

  const groups: ProductPickerGroup[] = [
    {
      label: 'Main Products',
      options: products.map((product) => ({
        label: product.name,
        value: isTickmillSubProductType(product.id)
          ? toStandaloneSubProductValue(product.id)
          : toProductValue(product.id),
      })),
    },
  ]

  products
    .filter((product) => !isTickmillSubProductType(product.id))
    .forEach((product) => {
      if (!subProducts.length) return

      groups.push({
        label: product.name,
        options: subProducts.map((subProduct) => ({
          label: `${product.name} > ${subProduct.name}`,
          value: toSubProductValue(product.id, subProduct.id),
        })),
      })
    })

  return groups
}

export const getSelectedProductPickerValues = (
  products: CountryPreferenceProduct[]
): ProductPickerValue[] => {
  return products.flatMap((product) => {
    if (isTickmillSubProductType(product.id)) {
      return [toStandaloneSubProductValue(product.id)]
    }

    const subProductValues = (product.subProducts ?? []).map((subProduct) =>
      toSubProductValue(product.id, subProduct.id)
    )

    return [toProductValue(product.id), ...subProductValues]
  })
}

export const mapSelectedProducts = (
  selectedValues: ProductPickerValue[],
  availableProducts: CountryPreferenceProduct[],
  existingProducts: CountryPreferenceProduct[] = []
): CountryPreferenceProduct[] => {
  const selectedProductIds = new Set<number>()
  const standaloneSubProductIds = new Set<number>()
  const subProductsByParent = new Map<number, Set<number>>()

  for (const value of selectedValues) {
    const parsed = parseValue(value)
    if (!parsed) continue

    if (parsed.type === 'product') {
      selectedProductIds.add(parsed.id)
      continue
    }

    if (parsed.type === 'standaloneSubProduct') {
      standaloneSubProductIds.add(parsed.id)
      continue
    }

    if (!subProductsByParent.has(parsed.parentId)) {
      subProductsByParent.set(parsed.parentId, new Set())
    }

    subProductsByParent.get(parsed.parentId)!.add(parsed.id)
  }

  for (const id of standaloneSubProductIds) {
    selectedProductIds.add(id)
  }

  for (const parentId of subProductsByParent.keys()) {
    selectedProductIds.add(parentId)
  }

  const orderedIds = [
    ...availableProducts.map((product) => product.id),
    ...existingProducts
      .map((product) => product.id)
      .filter((id) => !availableProducts.some((product) => product.id === id)),
  ]

  const result: CountryPreferenceProduct[] = []

  for (const productId of orderedIds) {
    if (!selectedProductIds.has(productId)) continue

    const product = findById(productId, availableProducts, existingProducts)
    if (!product) continue

    if (isTickmillSubProductType(product.id)) {
      result.push({
        ...product,
        subProducts: [],
      })
      continue
    }

    const selectedSubProductIds = [...(subProductsByParent.get(product.id) ?? new Set())]
    const existingProduct = existingProducts.find(
      (existingProduct) => existingProduct.id === product.id
    )

    const selectedSubProducts = selectedSubProductIds
      .map((subProductId) => findById(subProductId, availableProducts, existingProducts))
      .filter((subProduct): subProduct is CountryPreferenceProduct => Boolean(subProduct))
      .map((subProduct) => ({
        id: subProduct.id,
        name: subProduct.name,
        isActive:
          existingProduct?.subProducts?.find(
            (currentSubProduct) => currentSubProduct.id === subProduct.id
          )?.isActive ??
          product.subProducts?.find((currentSubProduct) => currentSubProduct.id === subProduct.id)
            ?.isActive ??
          false,
      }))

    result.push({
      ...product,
      subProducts: selectedSubProducts,
    })
  }

  return result
}

export const normalizeProductsForSubmission = (
  products: CountryPreferenceProduct[]
): CountryPreferenceProduct[] =>
  products.map((product) => ({
    id: product.id,
    name: product.name,
    subProducts: (product.subProducts ?? []).map((subProduct) => ({
      id: subProduct.id,
      name: subProduct.name,
      isActive: subProduct.isActive,
    })),
  }))

export const getProductDisplayEntries = (products: CountryPreferenceProduct[]): string[] => {
  return products.flatMap((product) => [
    product.name,
    ...(product.subProducts ?? []).map((subProduct) => `${product.name} > ${subProduct.name}`),
  ])
}
